import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getModules, ModuleId, TenantModule, updateModule } from '../api/client';
import { useAuth } from './AuthContext';

interface ModulesContextValue {
  modules: TenantModule[];
  loading: boolean;
  error: string | null;
  refreshModules: () => Promise<void>;
  setModuleEnabled: (moduleId: ModuleId, enabled: boolean) => Promise<TenantModule>;
  isModuleEnabled: (moduleId: ModuleId) => boolean;
  getModule: (moduleId: ModuleId) => TenantModule | undefined;
}

const ModulesContext = createContext<ModulesContextValue | undefined>(undefined);

export function ModulesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [modules, setModules] = useState<TenantModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshModules = useCallback(async () => {
    if (!user?.tenant_id) {
      setModules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await getModules();
      setModules(response.data.modules ?? []);
    } catch (err) {
      setError('load_failed');
      setModules([]);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id]);

  useEffect(() => {
    refreshModules();
  }, [refreshModules]);

  const setModuleEnabled = useCallback(async (moduleId: ModuleId, enabled: boolean) => {
    const response = await updateModule(moduleId, enabled);
    const updated = response.data;
    setModules((current) => current.map((module) => module.id === moduleId ? updated : module));
    return updated;
  }, []);

  const moduleMap = useMemo(
    () => new Map(modules.map((module) => [module.id, module])),
    [modules],
  );

  const value = useMemo<ModulesContextValue>(() => ({
    modules,
    loading,
    error,
    refreshModules,
    setModuleEnabled,
    isModuleEnabled: (moduleId: ModuleId) => moduleMap.get(moduleId)?.enabled === true,
    getModule: (moduleId: ModuleId) => moduleMap.get(moduleId),
  }), [modules, loading, error, refreshModules, setModuleEnabled, moduleMap]);

  return (
    <ModulesContext.Provider value={value}>
      {children}
    </ModulesContext.Provider>
  );
}

export function useModules() {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within ModulesProvider');
  }
  return context;
}

